import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuCode, LuShieldCheck, LuGlobe, LuZap } from 'react-icons/lu';
import HeaderOne from '../../../header/HeaderOne';
import FooterOne from '../../../footer/FooterOne';
import Head from '../../../components/Head';
import '../../landing.css';

const FEATURES = [
  { icon: <LuCode size={16} strokeWidth={2} />, title: 'REST + native SDKs', body: 'JavaScript, Python, PHP, C# clients. Or curl any endpoint — fully documented OpenAPI spec.' },
  { icon: <LuZap size={16} strokeWidth={2} />, title: 'Sub-second dispatch', body: 'Messages hit the carrier within seconds of your API call. Real-time status webhooks back to your app.' },
  { icon: <LuShieldCheck size={16} strokeWidth={2} />, title: 'Carrier-friendly throttling', body: 'We watch your number\'s standing and pace sends so carriers don\'t flag your traffic.' },
  { icon: <LuGlobe size={16} strokeWidth={2} />, title: 'Global delivery', body: '180+ countries. Pick the sender country to match your audience for higher answer rates.' },
];

export default function SmsApi() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="SMS API — Send Texts by REST from Any App"
        description="TextLink's SMS API lets you send text messages from any backend in seconds. JS, Python, PHP, C# SDKs. Status webhooks, global delivery, carrier-friendly throttling — and $0.001 per SMS through your own SIM."
        canonical="/smsapi"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell">

          <header className="lp-hero">
            <span className="lp-eyebrow">SMS API</span>
            <h1 className="lp-h1">
              Send SMS from any app.<br />
              <span className="lp-h1-accent">One REST call.</span>
            </h1>
            <p className="lp-lead">
              A clean, documented SMS API that works from any language. No 10DLC paperwork, no per-segment markup — just send through your own SIM at $0.001/message.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Get an API key <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <a className="lp-btn lp-btn--ghost" href="https://docs.textlinksms.com/" target="_blank" rel="noopener noreferrer">
                Read the docs
              </a>
            </div>
            <div className="lp-meta">
              <span>300 free messages</span>
              <span className="lp-meta-sep" />
              <span>No credit card</span>
              <span className="lp-meta-sep" />
              <span>$0.001 per SMS</span>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">A simple POST. That's it.</h2>
            <p className="lp-sub">Drop into any backend. Curl, fetch, axios, requests — whatever you've got.</p>
            <pre className="lp-code"><code>{`curl -X POST https://textlinksms.com/api/send-sms \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "+15551234567",
    "text": "Your code is 8472"
  }'`}</code></pre>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Built for production</h2>
            <p className="lp-sub">Everything you need to ship — not just send.</p>
            <ul className="lp-feat-list">
              {FEATURES.map((f, i) => (
                <li key={i} className="lp-feat">
                  <div className="lp-feat-icon">{f.icon}</div>
                  <div className="lp-feat-text">
                    <strong>{f.title}</strong>
                    <span>{f.body}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Webhooks for everything</h2>
            <p className="lp-sub">
              Status updates (queued → sent → delivered → failed) post back to your URL. Inbound replies hit your endpoint as soon as the SIM receives them — no polling.
            </p>
            <div className="lp-callout">
              <strong>Need bulk?</strong> Use the <Link to="/bulksmsapi" style={{ color: 'inherit', textDecoration: 'underline' }}>Bulk SMS API</Link> to pass an array of messages in one call. For OTP codes, the <Link to="/verificationapi" style={{ color: 'inherit', textDecoration: 'underline' }}>Verification API</Link> handles code generation and matching for you.
            </div>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">Ship in five minutes.</h2>
            <p className="lp-sub">Sign up, grab an API key, send your first SMS. 300 free messages on signup.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
